import {
    ArrowDownWideNarrow, ArrowLeftRight, ArrowUpNarrowWide, Ban, Briefcase, Building2, Calendar, Check,
    ChevronDown, Clock, ExternalLink, Fingerprint, Image as ImageIcon, Link, Linkedin, MapPin, Phone,
    Pencil, Plus, RefreshCw, RotateCcw, Search, ShieldAlert, Star, Store, TrendingDown,
    UserPlus, Users, WifiOff, X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    searchArticleEstablishments,
    scrapeArticleFounders,
    NewsItem,
    NewsStatus,
    LinkedEstablishment,
    RichEstablishment,
    RichEstablishmentOwner,
    FounderScrapeStatus,
    PickerSort,
    ScrapedImage,
    ImageScrapeOutcome,
} from "interfaces/restaurantNews";
import { FranchiseDoc } from "interfaces/establishments";
import { JevEventChip, JevExtraction, JevReviewBadge, JevScoreBadge } from "./jevParts";

/**
 * Everything the two news moderation pages have in common: the inline editors (category,
 * city, image, establishment link, franchise tag) and the row that assembles them.
 *
 * There are two pages on purpose — `/bo/news-articles` for the data back-office and
 * `/bo-commercial/news` for the sales one — but only one implementation of what a row *does*,
 * so an editor fixed on one is fixed on both. What differs between them is the shell around
 * the list: the header, the filters and the layout.
 */

// ─── helpers ──────────────────────────────────────────────────────────────────

export function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3_600_000);
    if (h < 1) return "À l'instant";
    if (h < 24) return `Il y a ${h}h`;
    const d = Math.floor(h / 24);
    if (d === 1) return "Hier";
    if (d < 7) return `Il y a ${d}j`;
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

// ─── week helpers (Monday-start ISO week, computed in UTC to match the backend) ───────────────

function mondayUTC(d: Date): Date {
    const day = d.getUTCDay() || 7; // Mon=1 … Sun=7
    const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    if (day !== 1) monday.setUTCDate(monday.getUTCDate() - (day - 1));
    return monday;
}

function toWeekParam(d: Date): string {
    return d.toISOString().slice(0, 10);
}

export function currentWeekParam(): string {
    return toWeekParam(mondayUTC(new Date()));
}

export function shiftWeek(weekStart: string, deltaWeeks: number): string {
    const d = new Date(`${weekStart}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + deltaWeeks * 7);
    return toWeekParam(d);
}

export function isoWeekNumber(weekStart: string): number {
    const d = new Date(`${weekStart}T00:00:00.000Z`);
    // nearest Thursday pins the date to the right ISO week-year at year boundaries
    const thursday = new Date(d);
    thursday.setUTCDate(d.getUTCDate() + 3);
    const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
    return Math.ceil((((thursday.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);
}

export function formatWeekRange(weekStart: string): string {
    const start = new Date(`${weekStart}T00:00:00.000Z`);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", timeZone: "UTC" });
    return `${fmt(start)} – ${fmt(end)}`;
}

export const CATEGORY_LABEL: Record<string, { label: string; text: string; bg: string; dot: string; icon: React.ElementType }> = {
    opening:     { label: "Ouverture",   text: "text-success", bg: "bg-success-subtle", dot: "bg-success", icon: Store },
    closing:     { label: "Fermeture",   text: "text-danger",  bg: "bg-danger-subtle",  dot: "bg-danger",  icon: TrendingDown },
    ownership:   { label: "Cession",     text: "text-warning", bg: "bg-warning-subtle", dot: "bg-warning", icon: ArrowLeftRight },
    recruitment: { label: "Recrutement", text: "text-info",    bg: "bg-info-subtle",    dot: "bg-info",    icon: UserPlus },
};

export const STATUS_STYLE: Record<NewsStatus, { rowBg: string; titleCls: string }> = {
    pending:  { rowBg: "hover:bg-surface-sunken/60",                titleCls: "text-ink" },
    approved: { rowBg: "bg-success-subtle/40 hover:bg-success-subtle/70", titleCls: "text-ink" },
    rejected: { rowBg: "bg-surface-sunken/60 opacity-60",           titleCls: "text-ink-faint line-through" },
};

export function CategoryBadge({ category }: { category: string }) {
    const cfg = CATEGORY_LABEL[category];
    if (!cfg) return null;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${cfg.text} ${cfg.bg}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
}

// ─── category inline edit ─────────────────────────────────────────────────────

function CategoryEdit({ category, onSave }: { category: "opening" | "closing" | "ownership" | "recruitment"; onSave: (category: "opening" | "closing" | "ownership" | "recruitment") => Promise<void> }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!editing) return;
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) setEditing(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [editing]);

    const handleSelect = async (newCategory: "opening" | "closing" | "ownership" | "recruitment") => {
        if (newCategory === category) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(newCategory);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const options: Array<{ value: "opening" | "closing" | "ownership" | "recruitment"; label: string; text: string; bg: string; dot: string }> = [
        { value: "opening", label: "Ouverture", text: "text-success", bg: "bg-success-subtle", dot: "bg-success" },
        { value: "closing", label: "Fermeture", text: "text-danger", bg: "bg-danger-subtle", dot: "bg-danger" },
        { value: "ownership", label: "Cession", text: "text-warning", bg: "bg-warning-subtle", dot: "bg-warning" },
        { value: "recruitment", label: "Recrutement", text: "text-info", bg: "bg-info-subtle", dot: "bg-info" },
    ];

    return (
        <div className="relative inline-block" ref={panelRef}>
            <button
                onClick={() => setEditing((e) => !e)}
                disabled={saving}
                className={`inline-flex items-center gap-1 rounded disabled:opacity-50 ${FOCUS_RING}`}
                title="Modifier la catégorie"
            >
                <CategoryBadge category={category} />
                <Pencil className="w-2.5 h-2.5 text-ink-faint hover:text-ink-muted transition-colors" />
            </button>

            {editing && (
                <div className="absolute left-0 top-full mt-1 z-30 w-36 bg-surface border border-border rounded-lg shadow-float overflow-hidden">
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => handleSelect(opt.value)}
                            disabled={saving}
                            className={`w-full text-left px-3 py-2 text-xs transition-colors duration-150 disabled:opacity-50 ${
                                category === opt.value ? `${opt.bg} ${opt.text} font-semibold` : "hover:bg-surface-sunken text-ink"
                            }`}
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} />
                                {opt.label}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function ScoreBadge({ score }: { score: number | null }) {
    if (score === null) return <span className="text-[10px] text-ink-faint">—</span>;
    const cls =
        score >= 90 ? "text-success bg-success-subtle" :
        score >= 60 ? "text-warning bg-warning-subtle" :
                      "text-ink-muted bg-surface-sunken";
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums font-mono ${cls}`}>
            {score}
        </span>
    );
}

// ─── city inline edit ─────────────────────────────────────────────────────────

function CityEdit({ city, onSave }: { city: string | null; onSave: (city: string | null) => Promise<void> }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(city ?? "");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editing) {
            setValue(city ?? "");
            inputRef.current?.focus();
        }
    }, [editing, city]);

    const save = async () => {
        const trimmed = value.trim() || null;
        if (trimmed === city) { setEditing(false); return; }
        setSaving(true);
        try {
            await onSave(trimmed);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") save();
        if (e.key === "Escape") setEditing(false);
    };

    if (editing) {
        return (
            <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3 text-ink-faint flex-shrink-0" />
                <input
                    ref={inputRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={save}
                    disabled={saving}
                    placeholder="Ville…"
                    className={`text-[11px] border border-border rounded px-1 py-0 w-24 bg-surface text-ink ${FOCUS_RING}`}
                />
            </span>
        );
    }

    if (city) {
        return (
            <button onClick={() => setEditing(true)}
                className={`inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink group rounded ${FOCUS_RING}`}
                title="Modifier la ville — corrigez si l'auto-détection s'est trompée">
                <MapPin className="w-3 h-3" />
                {city}
                <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-50 transition-opacity" />
            </button>
        );
    }

    return (
        <button onClick={() => setEditing(true)}
            className={`inline-flex items-center gap-0.5 text-[11px] text-ink-faint hover:text-ink-muted rounded ${FOCUS_RING}`}
            title="Ajouter une ville">
            <Plus className="w-3 h-3" />ville
        </button>
    );
}

// ─── source scrape status ─────────────────────────────────────────────────────

// Shown on the row when the last image scrape hit a wall — so the operator knows the empty
// gallery is the source blocking us (Cloudflare, anti-bot, captcha…), not a missing feature.
export function SourceScrapeBadge({ outcome, detail }: { outcome?: ImageScrapeOutcome | null; detail?: string | null }) {
    if (outcome !== "blocked" && outcome !== "error") return null;
    const isBlocked = outcome === "blocked";
    const Icon = isBlocked ? ShieldAlert : WifiOff;
    const label = isBlocked ? "Source bloquée" : "Page inaccessible";
    return (
        <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-danger bg-danger-subtle"
            title={
                isBlocked
                    ? `Le site source renvoie une page anti-bot${detail ? ` (${detail})` : ""} — impossible d'en récupérer les images automatiquement. Colle une URL d'image à la main.`
                    : `La page source n'a pas pu être chargée${detail ? ` (${detail})` : ""}.`
            }
        >
            <Icon className="w-2.5 h-2.5" />
            {label}{detail ? ` · ${detail}` : ""}
        </span>
    );
}

// ─── preview image picker ──────────────────────────────────────────────────────

/**
 * The preview image of an article. A human picks one from the gallery the scraper built off
 * the source page (og:image, twitter:image, JSON-LD, body images), or pastes a URL by hand.
 * "Scraper la page" (re)runs the scraper on demand — the CTA for older articles whose images
 * were never fetched.
 */
function ImagePicker({
    image,
    scrapedImages,
    imagesScrapedAt,
    scrapeOutcome,
    scrapeDetail,
    onSave,
    onScrape,
}: {
    image: string | null;
    scrapedImages: ScrapedImage[];
    imagesScrapedAt: string | null;
    scrapeOutcome?: ImageScrapeOutcome | null;
    scrapeDetail?: string | null;
    onSave: (image: string | null) => Promise<void>;
    onScrape: () => Promise<void>;
}) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [scraping, setScraping] = useState(false);
    const [showUrl, setShowUrl] = useState(false);
    const [value, setValue] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [broken, setBroken] = useState<Set<string>>(new Set());
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setValue("");
        setError(null);
        setShowUrl(false);
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    useEffect(() => {
        if (showUrl) setTimeout(() => inputRef.current?.focus(), 50);
    }, [showUrl]);

    const markBroken = (url: string) => setBroken((prev) => new Set(prev).add(url));

    // The chosen image, plus every scraped candidate, deduped and current-first.
    const gallery: ScrapedImage[] = [];
    const seen = new Set<string>();
    if (image) { gallery.push({ url: image, kind: "content", width: null, height: null, alt: null }); seen.add(image); }
    for (const img of scrapedImages) {
        if (!seen.has(img.url)) { gallery.push(img); seen.add(img.url); }
    }
    const visible = gallery.filter((g) => !broken.has(g.url));

    const pick = async (url: string | null) => {
        setSaving(true);
        setError(null);
        try {
            await onSave(url);
            setOpen(false);
        } catch {
            setError("Échec de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const saveUrl = async () => {
        const trimmed = value.trim();
        if (!trimmed) return;
        if (!/^https?:\/\//i.test(trimmed)) {
            setError("L'URL doit commencer par http:// ou https://");
            return;
        }
        await pick(trimmed);
    };

    const runScrape = async () => {
        setScraping(true);
        setError(null);
        try {
            await onScrape();
        } catch {
            setError("Échec du scraping");
        } finally {
            setScraping(false);
        }
    };

    const neverScraped = !imagesScrapedAt;
    const blocked = scrapeOutcome === "blocked";
    const errored = scrapeOutcome === "error";
    const noResults = !neverScraped && !blocked && !errored && scrapedImages.length === 0;

    return (
        <div className="relative flex-shrink-0" ref={panelRef}>
            <button
                onClick={() => setOpen((o) => !o)}
                className={`group relative w-14 h-10 rounded-md overflow-hidden block ${FOCUS_RING}`}
                title="Choisir l'image de preview"
            >
                {image ? (
                    <img src={image} alt="" className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-surface-sunken">
                        <ImageIcon className="w-3.5 h-3.5 text-ink-faint" />
                    </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-ink/0 group-hover:bg-ink/40 transition-colors duration-150">
                    <Pencil className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {/* source blocked / unreachable, and no preview to fall back on */}
                {!image && (scrapeOutcome === "blocked" || scrapeOutcome === "error") ? (
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-danger ring-2 ring-surface" />
                ) : !image && scrapedImages.length > 0 && (
                    /* a preview is waiting to be chosen from the scraped candidates */
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-warning ring-2 ring-surface" />
                )}
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-1.5 z-30 w-80 bg-surface border border-border rounded-2xl shadow-float overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-ink">Image de preview</p>
                            <p className={`text-[10px] ${blocked || errored ? "text-danger" : "text-ink-faint"}`}>
                                {neverScraped
                                    ? "Page pas encore scrapée"
                                    : blocked
                                        ? `Source bloquée${scrapeDetail ? ` · ${scrapeDetail}` : ""}`
                                        : errored
                                            ? `Page inaccessible${scrapeDetail ? ` · ${scrapeDetail}` : ""}`
                                            : `${scrapedImages.length} image${scrapedImages.length > 1 ? "s" : ""} trouvée${scrapedImages.length > 1 ? "s" : ""}`}
                            </p>
                        </div>
                        <button
                            onClick={runScrape}
                            disabled={scraping}
                            title="Scraper (à nouveau) les images de la page source"
                            className={`inline-flex items-center gap-1 flex-shrink-0 text-[11px] font-medium border border-border-strong bg-surface hover:bg-surface-sunken px-2 py-1 rounded-lg disabled:opacity-50 transition-colors ${FOCUS_RING}`}
                        >
                            <RefreshCw className={`w-3 h-3 ${scraping ? "animate-spin" : ""}`} />
                            {scraping ? "…" : neverScraped ? "Scraper" : "Rescraper"}
                        </button>
                    </div>

                    <div className="p-3 space-y-2.5 max-h-[22rem] overflow-y-auto">
                        {visible.length > 0 ? (
                            <div className="grid grid-cols-3 gap-1.5">
                                {visible.map((img) => {
                                    const selected = img.url === image;
                                    return (
                                        <button
                                            key={img.url}
                                            onClick={() => pick(img.url)}
                                            disabled={saving}
                                            title={img.alt || img.url}
                                            className={`group relative aspect-[4/3] rounded-lg overflow-hidden bg-surface-sunken transition-all disabled:opacity-50 ${FOCUS_RING} ${
                                                selected ? "ring-2 ring-accent" : "ring-1 ring-border hover:ring-border-strong"
                                            }`}
                                        >
                                            <img
                                                src={img.url}
                                                alt=""
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                                onError={() => markBroken(img.url)}
                                            />
                                            {selected && (
                                                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-accent flex items-center justify-center">
                                                    <Check className="w-2.5 h-2.5 text-ink-inverse" />
                                                </span>
                                            )}
                                            {img.kind === "og" && !selected && (
                                                <span className="absolute bottom-0 inset-x-0 bg-ink/60 text-ink-inverse text-[8px] font-semibold text-center py-px uppercase tracking-wide">
                                                    og:image
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : blocked ? (
                            <div className="flex items-start gap-2 rounded-lg bg-danger-subtle px-2.5 py-2 text-danger">
                                <ShieldAlert className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                                <p className="text-[11px] leading-snug">
                                    Le site source renvoie une page anti-bot{scrapeDetail ? ` (${scrapeDetail})` : ""} : ses
                                    images ne sont pas récupérables automatiquement. Ouvre l'article et colle l'URL d'une image ci-dessous.
                                </p>
                            </div>
                        ) : errored ? (
                            <div className="flex items-start gap-2 rounded-lg bg-danger-subtle px-2.5 py-2 text-danger">
                                <WifiOff className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                                <p className="text-[11px] leading-snug">
                                    La page source n'a pas pu être chargée{scrapeDetail ? ` (${scrapeDetail})` : ""}. Réessaie le scraping ou colle une URL.
                                </p>
                            </div>
                        ) : (
                            <p className="text-[11px] text-ink-faint text-center py-3">
                                {neverScraped
                                    ? "Lance le scraping pour récupérer les images de la page."
                                    : noResults
                                        ? "Aucune image trouvée sur la page source."
                                        : "Aucune image affichable."}
                            </p>
                        )}

                        <div className="flex items-center justify-between gap-2 pt-0.5">
                            {image ? (
                                <button onClick={() => pick(null)} disabled={saving}
                                    className={`text-[11px] text-ink-muted hover:text-danger transition-colors rounded ${FOCUS_RING}`}>
                                    Retirer l'image
                                </button>
                            ) : <span />}
                            <button onClick={() => setShowUrl((s) => !s)}
                                className={`text-[11px] text-ink-muted hover:text-ink transition-colors rounded ${FOCUS_RING}`}>
                                {showUrl ? "Masquer" : "Coller une URL"}
                            </button>
                        </div>

                        {showUrl && (
                            <div className="space-y-2 pt-1 border-t border-border">
                                <input
                                    ref={inputRef}
                                    value={value}
                                    onChange={(e) => { setValue(e.target.value); setError(null); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveUrl(); if (e.key === "Escape") setShowUrl(false); }}
                                    placeholder="https://…"
                                    className={`w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-surface text-ink placeholder:text-ink-faint ${FOCUS_RING}`}
                                />
                                {value.trim() && /^https?:\/\//i.test(value.trim()) && (
                                    <div className="w-full h-20 rounded-lg overflow-hidden bg-surface-sunken">
                                        <img src={value.trim()} alt="Aperçu" className="w-full h-full object-cover"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                    </div>
                                )}
                                <button onClick={saveUrl} disabled={saving || !value.trim()}
                                    className={`w-full inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-medium bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors disabled:opacity-40 ${FOCUS_RING}`}>
                                    {saving ? "…" : "Utiliser cette URL"}
                                </button>
                            </div>
                        )}

                        {error && <p className="text-[11px] text-danger">{error}</p>}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── establishment link ───────────────────────────────────────────────────────

function scoreTone(score: number): string {
    if (score >= 75) return "text-success bg-success-subtle";
    if (score >= 45) return "text-warning bg-warning-subtle";
    return "text-ink-muted bg-surface-sunken";
}

function MatchScoreBadge({ score, reasons, isDirectMatch }: { score: number; reasons?: string[]; isDirectMatch?: boolean }) {
    const tooltip = reasons?.join(" · ") ?? "";
    if (isDirectMatch) {
        return (
            <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 text-accent bg-accent-subtle border border-accent/20"
                title={tooltip || "SIRET/SIREN identifié dans l'article — correspondance quasi certaine"}
            >
                <Fingerprint className="w-2.5 h-2.5" />
                SIRET
            </span>
        );
    }
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${scoreTone(score)}`} title={tooltip}>
            {score}%
        </span>
    );
}

function fmtDate(d?: string | null): string | null {
    if (!d) return null;
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

// The date the picker sorts / labels an establishment on — its opening, or the company's
// registration if the opening was never recorded.
function effectiveOpenDate(est: RichEstablishment): string | null {
    return est.operations?.opening_date ?? est.registration?.creation_date ?? null;
}

// "il y a 2 jours" / "il y a 3 semaines" / "il y a 3 ans" — coarse, human, no clock time.
function openedAgo(d?: string | null): string | null {
    if (!d) return null;
    const t = new Date(d).getTime();
    if (Number.isNaN(t)) return null;
    const days = Math.floor((Date.now() - t) / 86_400_000);
    if (days < 0) return "à venir";
    if (days === 0) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 7) return `il y a ${days} jours`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `il y a ${weeks} semaine${weeks > 1 ? "s" : ""}`;
    const months = Math.round(days / 30.44);
    if (months < 12) return `il y a ${months} mois`;
    const years = Math.floor(days / 365.25);
    const rem = Math.round((days - years * 365.25) / 30.44);
    if (years < 4 && rem >= 1 && rem <= 11) return `il y a ${years} an${years > 1 ? "s" : ""} et ${rem} mois`;
    return `il y a ${years} an${years > 1 ? "s" : ""}`;
}

function ownerPortfolioLabel(o: RichEstablishmentOwner): string {
    const parts: string[] = [];
    if (o.activeEstablishments > 0)
        parts.push(`${o.activeEstablishments} établissement${o.activeEstablishments > 1 ? "s" : ""} actif${o.activeEstablishments > 1 ? "s" : ""}`);
    if (o.closedEstablishments > 0)
        parts.push(`${o.closedEstablishments} fermé${o.closedEstablishments > 1 ? "s" : ""}`);
    return parts.join(" · ");
}

function regStatusLabel(status: RichEstablishment["registration"]["status"]): string {
    switch (status) {
        case "active": return "Active";
        case "closed": return "Fermée";
        case "in_liquidation": return "En liquidation";
        case "other": return "Autre";
        default: return "—";
    }
}

function InfoRow({ icon: Icon, label, children }: { icon: any; label: string; children: React.ReactNode }) {
    return (
        <div className="flex gap-2.5 py-1.5">
            <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-ink-faint" />
            <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">{label}</p>
                <div className="text-xs text-ink break-words">{children}</div>
            </div>
        </div>
    );
}

function EstablishmentDetailPane({
    est,
    isLinked,
    saving,
    articleCity,
    onLink,
    onUnlink,
}: {
    est: RichEstablishment;
    isLinked: boolean;
    saving: boolean;
    articleCity: string | null;
    onLink: () => void;
    onUnlink: () => void;
}) {
    const reg = est.registration;
    const addr = est.address;
    const pappersUrl = pappersCompanyUrl(reg.siren) ?? pappersSearchUrl(est.name, addr?.city || articleCity || "");
    const mapsUrl = mapsSearchUrl(est.name, addr?.city || articleCity || "");
    const fullAddress = [
        addr?.street,
        [addr?.postcode, addr?.city].filter(Boolean).join(" "),
        addr?.region,
        addr?.country && addr.country !== "France" ? addr.country : null,
    ].filter(Boolean).join(", ");
    const capital = reg.capital_eur != null ? `${reg.capital_eur.toLocaleString("fr-FR")} €` : null;
    const tagline = [
        est.type,
        reg.enseigne && `Enseigne : ${reg.enseigne}`,
        est.franchise?.name && `Franchise : ${est.franchise.name}`,
        est.brand_group,
    ].filter(Boolean).join(" · ");

    return (
        <div className="flex h-full flex-col">
            {/* header + CTA */}
            <div className="flex items-start gap-3 border-b border-border px-6 py-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-ink">{est.name}</h3>
                        {!est.is_active && (
                            <span className="rounded bg-danger-subtle px-1.5 py-0.5 text-[10px] font-semibold text-danger">Fermé</span>
                        )}
                        {est.matchScore != null && (
                            <MatchScoreBadge score={est.matchScore} reasons={est.matchReasons} isDirectMatch={est.isDirectMatch} />
                        )}
                    </div>
                    {est.legal_name && est.legal_name !== est.name && (
                        <p className="mt-0.5 text-[11px] text-ink-faint">Raison sociale : {est.legal_name}</p>
                    )}
                    <p className="mt-0.5 text-xs text-ink-muted">{tagline || "—"}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {[
                            { href: `/establishments/${est._id}`, label: "Fiche interne" },
                            { href: pappersUrl, label: "Pappers" },
                            { href: mapsUrl, label: "Google Maps" },
                        ].map((l) => (
                            <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                                className={`inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}>
                                <ExternalLink className="h-3 w-3" /> {l.label}
                            </a>
                        ))}
                    </div>
                </div>
                {isLinked ? (
                    <button onClick={onUnlink} disabled={saving}
                        className={`flex-shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-sunken disabled:opacity-40 ${FOCUS_RING}`}>
                        Dissocier
                    </button>
                ) : (
                    <button onClick={onLink} disabled={saving}
                        className={`flex-shrink-0 rounded-lg bg-ink px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-ink/90 disabled:opacity-40 ${FOCUS_RING}`}>
                        {saving ? "…" : "Lier cet établissement"}
                    </button>
                )}
            </div>

            {/* scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
                    <InfoRow icon={MapPin} label="Adresse complète">{fullAddress || "—"}</InfoRow>
                    <InfoRow icon={Calendar} label="Ouverture">
                        {(() => {
                            const d = est.operations.opening_date ?? est.registration.creation_date;
                            const ago = openedAgo(d);
                            return (
                                <>
                                    {fmtDate(est.operations.opening_date) || (est.registration.creation_date ? `${fmtDate(est.registration.creation_date)} (immatriculation)` : "—")}
                                    {ago && <span className="text-ink-muted"> · {ago}</span>}
                                </>
                            );
                        })()}
                        {est.operations.closing_date && (
                            <span className="text-danger"> · fermé le {fmtDate(est.operations.closing_date)}</span>
                        )}
                    </InfoRow>
                    <InfoRow icon={Fingerprint} label="SIREN / SIRET">
                        {reg.siren || "—"}{reg.siret ? ` · ${reg.siret}` : ""}
                    </InfoRow>
                    <InfoRow icon={Building2} label="Forme juridique">
                        {[reg.legal_form, capital && `capital ${capital}`].filter(Boolean).join(" · ") || "—"}
                    </InfoRow>
                    <InfoRow icon={Briefcase} label="Activité (NAF)">
                        {reg.naf_code
                            ? `${reg.naf_code}${reg.naf_description ? ` — ${reg.naf_description}` : ""}`
                            : est.primary_declared_activity || "—"}
                    </InfoRow>
                    <InfoRow icon={Users} label="Effectif / taille">
                        {[reg.workforce_range, reg.company_size_category].filter(Boolean).join(" · ") || "—"}
                    </InfoRow>
                    <InfoRow icon={Calendar} label="Immatriculation">
                        {[fmtDate(reg.creation_date), reg.rcs_city && `RCS ${reg.rcs_city}`].filter(Boolean).join(" · ") || "—"}
                    </InfoRow>
                    <InfoRow icon={Store} label="Statut RCS">{regStatusLabel(reg.status)}</InfoRow>
                    {(est.phone || est.email || est.website_url) && (
                        <InfoRow icon={Phone} label="Contact">
                            <div className="flex flex-col gap-0.5">
                                {est.phone && <span>{est.phone}</span>}
                                {est.email && <span className="break-all">{est.email}</span>}
                                {est.website_url && (
                                    <a href={est.website_url} target="_blank" rel="noopener noreferrer"
                                        className="text-accent hover:underline break-all">
                                        {est.website_url}
                                    </a>
                                )}
                            </div>
                        </InfoRow>
                    )}
                    {est.ratings.google_avg != null && (
                        <InfoRow icon={Star} label="Note Google">
                            {est.ratings.google_avg.toFixed(1)}/5
                            {est.ratings.google_count ? ` · ${est.ratings.google_count} avis` : ""}
                        </InfoRow>
                    )}
                    {(est.categories.length > 0 || est.subCategories.length > 0) && (
                        <InfoRow icon={Briefcase} label="Catégories">
                            {[...est.categories, ...est.subCategories].join(", ")}
                        </InfoRow>
                    )}
                </div>

                {/* dirigeants */}
                <div className="mt-5">
                    <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                        <Users className="h-3.5 w-3.5" /> Dirigeants ({est.owners.length})
                    </p>
                    {est.owners.length === 0 ? (
                        <p className="text-xs text-ink-faint">Aucun dirigeant renseigné</p>
                    ) : (
                        <ul className="space-y-2">
                            {est.owners.map((o) => (
                                <li key={o._id} className="rounded-lg border border-border bg-canvas px-3 py-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-medium text-ink">{o.name}</span>
                                        {o.qualite && <span className="text-[11px] text-ink-muted">{o.qualite}</span>}
                                        {o.role === "primary" && (
                                            <span className="rounded bg-surface-sunken px-1 py-px text-[9px] font-semibold uppercase text-ink-muted">
                                                Principal
                                            </span>
                                        )}
                                        {o.linkedinUrl && (
                                            <a href={o.linkedinUrl} target="_blank" rel="noopener noreferrer"
                                                className="text-ink-faint hover:text-accent" title="Profil LinkedIn">
                                                <Linkedin className="h-3.5 w-3.5" />
                                            </a>
                                        )}
                                    </div>
                                    {(o.age != null || ownerPortfolioLabel(o)) && (
                                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-ink-faint">
                                            {o.age != null && <span>{o.age} ans</span>}
                                            {ownerPortfolioLabel(o) && <span>{ownerPortfolioLabel(o)}</span>}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {est.matchReasons && est.matchReasons.length > 0 && (
                    <div className="mt-5 rounded-lg bg-surface-sunken px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                            Pourquoi cette suggestion
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-muted">{est.matchReasons.join(" · ")}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── external lookups ─────────────────────────────────────────────────────────

function pappersSearchUrl(name: string, city: string): string {
    const q = [name, city].map((s) => s.trim()).filter(Boolean).join(" ");
    return `https://www.pappers.fr/recherche?q=${encodeURIComponent(q)}`;
}

function pappersCompanyUrl(siren?: string | null): string | null {
    const s = (siren ?? "").replace(/\s/g, "");
    return /^\d{9}$/.test(s) ? `https://www.pappers.fr/entreprise/${s}` : null;
}

function mapsSearchUrl(name: string, city: string): string {
    const q = [name, city].map((s) => s.trim()).filter(Boolean).join(" ");
    return `https://www.google.com/maps/search/${encodeURIComponent(q)}`;
}

const foldStr = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// ─── one filter field in the picker's search panel ────────────────────────────

function FilterField({
    label,
    value,
    onChange,
    placeholder,
    detected,
    suggestions,
    mono,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    detected?: boolean;
    suggestions?: string[];
    mono?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const opts = suggestions ?? [];
    // Offer the picker only when there is actually another value to choose.
    const showPicker = opts.length > 1 || (opts.length === 1 && foldStr(opts[0]) !== foldStr(value));

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    return (
        <div className="relative min-w-0" ref={rootRef}>
            <div className="mb-1 flex items-center gap-1.5 px-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
                {detected && value.trim() !== "" && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-accent">détecté</span>
                )}
                {showPicker && (
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        className={`ml-auto flex items-center gap-0.5 rounded text-[10px] font-medium text-ink-faint hover:text-ink ${FOCUS_RING}`}
                    >
                        {opts.length} proposition{opts.length > 1 ? "s" : ""}
                    </button>
                )}
            </div>
            <div className="flex h-8 items-center gap-1 rounded-lg border border-border bg-surface pl-2.5 pr-1 transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25">
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); }
                        if (e.key === "ArrowDown" && showPicker) { e.preventDefault(); setOpen(true); }
                    }}
                    placeholder={placeholder}
                    className={`min-w-0 flex-1 bg-transparent text-xs text-ink outline-none placeholder:text-ink-faint ${mono ? "font-mono tracking-tight" : ""}`}
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        title="Effacer"
                        className={`flex-shrink-0 rounded p-0.5 text-ink-faint hover:text-ink ${FOCUS_RING}`}
                    >
                        <X className="h-3 w-3" />
                    </button>
                )}
                {showPicker && (
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        title="Propositions de l'article"
                        className={`flex-shrink-0 rounded p-0.5 text-ink-faint hover:text-ink ${FOCUS_RING}`}
                    >
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                )}
            </div>

            {open && showPicker && (
                <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-float">
                    <li className="px-2.5 pb-1 pt-0.5 text-[9px] font-semibold uppercase tracking-wide text-ink-faint">
                        Détecté dans l'article
                    </li>
                    {opts.map((s) => {
                        const active = foldStr(s) === foldStr(value);
                        return (
                            <li key={s}>
                                <button
                                    type="button"
                                    onClick={() => { onChange(s); setOpen(false); }}
                                    className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-surface-sunken ${
                                        active ? "font-medium text-ink" : "text-ink-muted"
                                    }`}
                                >
                                    <span className="truncate">{s}</span>
                                    {active && <Check className="h-3 w-3 flex-shrink-0 text-accent" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

// Full-page picker — filter by owner name, restaurant name, city and SIREN/SIRET (only the
// city is pre-filled; names detected in the article are offered as suggestions), with the full
// establishment sheet shown before attribution. Closed establishments are hidden unless the operator opts in.
function EstablishmentPickerModal({
    articleId,
    articleTitle,
    articleCity,
    articleLink,
    linkedId,
    onPick,
    onClose,
}: {
    articleId: string;
    articleTitle: string;
    articleCity: string | null;
    articleLink?: string;
    linkedId: string | null;
    onPick: (est: RichEstablishment | null) => Promise<void>;
    onClose: () => void;
}) {
    const [owner, setOwner] = useState("");
    const [name, setName] = useState("");
    // Only the article's city is pre-filled — owner / restaurant stay empty, the detected names
    // are one click away in each field's suggestions.
    const [city, setCity] = useState(articleCity ?? "");
    const [reg, setReg] = useState("");
    const [includeClosed, setIncludeClosed] = useState(false);
    // Default to "most recently opened first" — an article about an opening is almost always
    // about a fresh establishment. A SIRET-in-text match still stays pinned on top server-side.
    const [sortKey, setSortKey] = useState<PickerSort>("date");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    const [results, setResults] = useState<RichEstablishment[]>([]);
    const [atLimit, setAtLimit] = useState(false);
    const [detectedNames, setDetectedNames] = useState<string[]>([]);
    const [detectedRestaurants, setDetectedRestaurants] = useState<string[]>([]);
    const [founderScrape, setFounderScrape] = useState<FounderScrapeStatus | null>(null);
    const [scrapingFounders, setScrapingFounders] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const founderScrapeTriedRef = useRef(false);

    const cityGuess = articleCity ?? "";
    // City is pre-filled but only *narrows* the scored auto-matches (backend `hardFilters`) —
    // so the header still reads "Correspondances détectées" until a name/owner/SIREN is set.
    const hasFilters = !!(owner.trim() || name.trim() || reg.trim());

    const run = useCallback(() => {
        setLoading(true);
        searchArticleEstablishments(articleId, {
            owner: owner.trim(),
            name: name.trim(),
            city: city.trim(),
            reg: reg.trim(),
            includeClosed,
            sort: sortKey,
            dir: sortDir,
        })
            .then((data) => {
                setDetectedNames(data.detectedNames ?? []);
                setDetectedRestaurants(data.detectedRestaurantNames ?? []);
                setFounderScrape(data.founderScrape ?? null);
                setResults(data.establishments);
                setAtLimit(data.establishments.length >= 25);
                setSelectedId((prev) =>
                    data.establishments.some((e) => e._id === prev)
                        ? prev
                        : data.establishments[0]?._id ?? null
                );
            })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [articleId, owner, name, city, reg, includeClosed, sortKey, sortDir]);

    useEffect(() => {
        const t = setTimeout(run, 280);
        return () => clearTimeout(t);
    }, [run]);

    // (Re)scrape the article body for names — auto once when it was never scraped, or on demand.
    const scrapeFounders = useCallback(() => {
        setScrapingFounders(true);
        scrapeArticleFounders(articleId)
            .then((data) => {
                setDetectedNames(data.detectedNames ?? []);
                setDetectedRestaurants(data.detectedRestaurantNames ?? []);
                setFounderScrape(data.founderScrape ?? null);
            })
            .catch(() => {})
            .finally(() => setScrapingFounders(false));
    }, [articleId]);

    useEffect(() => {
        if (founderScrapeTriedRef.current || loading || founderScrape === null) return;
        founderScrapeTriedRef.current = true;
        if (!founderScrape.scrapedAt) scrapeFounders();
    }, [loading, founderScrape, scrapeFounders]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const selected = results.find((e) => e._id === selectedId) ?? null;

    const resetToDetected = () => {
        setOwner("");
        setName("");
        setCity(cityGuess);
        setReg("");
        setIncludeClosed(false);
    };

    const pappersName = name.trim() || detectedRestaurants[0] || "";
    const pappersCity = city.trim() || cityGuess;
    const pappersQuery = `${pappersName} ${pappersCity}`.trim();

    const pick = async (est: RichEstablishment | null) => {
        setSaving(true);
        try {
            await onPick(est);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex flex-col bg-ink/50 p-3 sm:p-6" onMouseDown={onClose}>
            <div
                className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-surface shadow-float"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* header */}
                <div className="flex items-start gap-3 border-b border-border px-5 py-4">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold text-ink">Lier un établissement</h2>
                        <p className="mt-0.5 line-clamp-1 text-xs text-ink-muted">
                            {articleTitle}
                            {articleCity && <span className="text-ink-faint"> · {articleCity}</span>}
                        </p>
                    </div>
                    {articleLink && (
                        <a href={articleLink} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-ink-muted hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}>
                            <ExternalLink className="h-3.5 w-3.5" /> Voir l'article
                        </a>
                    )}
                    <button onClick={onClose}
                        className={`rounded-md p-1 text-ink-faint hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}>
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* filter panel */}
                <div className="border-b border-border bg-canvas px-5 py-3.5">
                    <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-4">
                        <FilterField
                            label="Propriétaire / gérant"
                            value={owner}
                            onChange={setOwner}
                            placeholder="Nom du dirigeant"
                            detected={detectedNames.some((n) => foldStr(n) === foldStr(owner))}
                            suggestions={detectedNames}
                        />
                        <FilterField
                            label="Nom du restaurant"
                            value={name}
                            onChange={setName}
                            placeholder="Enseigne, raison sociale"
                            detected={detectedRestaurants.some((n) => foldStr(n) === foldStr(name))}
                            suggestions={detectedRestaurants}
                        />
                        <FilterField
                            label="Ville"
                            value={city}
                            onChange={setCity}
                            placeholder="Commune"
                            detected={!!cityGuess && foldStr(city) === foldStr(cityGuess)}
                        />
                        <FilterField
                            label="SIREN / SIRET"
                            value={reg}
                            onChange={(v) => setReg(v.replace(/[^\d\s.]/g, ""))}
                            placeholder="9 ou 14 chiffres"
                            mono
                        />
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <a
                            href={pappersSearchUrl(pappersName, pappersCity)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-disabled={!pappersQuery}
                            onClick={(e) => { if (!pappersQuery) e.preventDefault(); }}
                            title={pappersQuery ? `Pappers « ${pappersQuery} »` : "Renseignez au moins le nom ou la ville"}
                            className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-medium transition-colors ${FOCUS_RING} ${
                                pappersQuery
                                    ? "border-border bg-surface text-ink hover:border-border-strong"
                                    : "cursor-not-allowed border-transparent bg-surface/50 text-ink-faint"
                            }`}
                        >
                            <ExternalLink className="h-3.5 w-3.5" /> Rechercher sur Pappers
                        </a>
                        <button
                            type="button"
                            onClick={resetToDetected}
                            title="Rétablir la ville de l'article et vider les autres filtres"
                            className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[11px] text-ink-muted transition-colors hover:bg-surface hover:text-ink ${FOCUS_RING}`}
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
                        </button>

                        <label className="ml-auto flex cursor-pointer select-none items-center gap-1.5 text-[11px] text-ink-muted">
                            <input
                                type="checkbox"
                                checked={includeClosed}
                                onChange={(e) => setIncludeClosed(e.target.checked)}
                                className="h-3.5 w-3.5 rounded border-border accent-accent"
                            />
                            Afficher les établissements fermés
                        </label>
                        {loading && <RefreshCw className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-ink-faint" />}
                    </div>
                </div>

                {/* founder-name scrape status — mirrors the image scraper's badges */}
                {(scrapingFounders || founderScrape) && (
                    <div className="flex items-center gap-2 border-b border-border px-5 py-1.5 text-[11px]">
                        {scrapingFounders ? (
                            <span className="flex items-center gap-1.5 text-ink-faint">
                                <RefreshCw className="h-3 w-3 animate-spin" />
                                Analyse du contenu de l'article…
                            </span>
                        ) : founderScrape?.outcome === "blocked" || founderScrape?.outcome === "error" ? (
                            <>
                                <SourceScrapeBadge outcome={founderScrape.outcome} detail={founderScrape.detail} />
                                <span className="text-ink-faint">
                                    contenu de l'article illisible{detectedNames.length + detectedRestaurants.length > 0 ? " — infos tirées du titre" : ""}
                                </span>
                            </>
                        ) : (
                            <span className="text-ink-faint">
                                {[
                                    detectedNames.length > 0 && `${detectedNames.length} gérant${detectedNames.length > 1 ? "s" : ""}`,
                                    detectedRestaurants.length > 0 && `${detectedRestaurants.length} nom${detectedRestaurants.length > 1 ? "s" : ""} d'enseigne`,
                                ].filter(Boolean).join(" · ") || "Aucune info identifiée dans l'article"}
                                {" "}— extraits de l'article
                            </span>
                        )}
                        {!scrapingFounders && (
                            <button
                                onClick={scrapeFounders}
                                title="Re-télécharger la page source et ré-extraire les informations"
                                className={`ml-auto flex items-center gap-1 text-ink-faint hover:text-ink ${FOCUS_RING}`}
                            >
                                <RefreshCw className="h-3 w-3" /> Ré-analyser l'article
                            </button>
                        )}
                    </div>
                )}

                {/* body */}
                <div className="flex min-h-0 flex-1">
                    <div className="relative w-[300px] flex-shrink-0 overflow-y-auto border-r border-border">
                        <div className="sticky top-0 z-10 border-b border-border bg-surface px-4 py-2">
                            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                                {loading
                                    ? "Recherche…"
                                    : hasFilters
                                        ? `${results.length}${atLimit ? "+" : ""} résultat${results.length > 1 ? "s" : ""}`
                                        : "Correspondances détectées"}
                                {loading && <RefreshCw className="h-3 w-3 animate-spin text-ink-faint" />}
                            </p>
                            <div className="mt-1.5 flex items-center gap-1">
                                <span className="text-[10px] uppercase tracking-wide text-ink-faint">Trier</span>
                                <select
                                    value={sortKey}
                                    onChange={(e) => setSortKey(e.target.value as PickerSort)}
                                    className={`rounded border border-border bg-surface px-1.5 py-0.5 text-[11px] text-ink outline-none focus:border-accent ${FOCUS_RING}`}
                                >
                                    <option value="relevance">Pertinence</option>
                                    <option value="date">Ouverture</option>
                                    <option value="name">Nom</option>
                                </select>
                                {sortKey !== "relevance" && (
                                    <button
                                        type="button"
                                        onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                                        title={
                                            sortKey === "date"
                                                ? sortDir === "desc" ? "Plus récent d'abord" : "Plus ancien d'abord"
                                                : sortDir === "desc" ? "Z → A" : "A → Z"
                                        }
                                        className={`flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-ink-muted hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}
                                    >
                                        {sortDir === "desc" ? <ArrowDownWideNarrow className="h-3.5 w-3.5" /> : <ArrowUpNarrowWide className="h-3.5 w-3.5" />}
                                        {sortKey === "date" && (sortDir === "desc" ? "récent" : "ancien")}
                                    </button>
                                )}
                            </div>
                        </div>
                        {results.length === 0 ? (
                            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-xs text-ink-faint">
                                {loading ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                        Recherche…
                                    </>
                                ) : hasFilters ? (
                                    "Aucun établissement pour ces critères"
                                ) : (
                                    "Aucune correspondance automatique"
                                )}
                            </div>
                        ) : (
                            <ul className={`pb-2 transition-opacity ${loading ? "pointer-events-none opacity-40" : ""}`}>
                                {results.map((est) => {
                                    const pappers = pappersCompanyUrl(est.registration.siren);
                                    const firstOwner = est.owners[0]?.name;
                                    const meta = [
                                        firstOwner,
                                        est.registration.siren && `SIREN ${est.registration.siren}`,
                                        est.franchise?.name,
                                    ].filter(Boolean).join(" · ");
                                    return (
                                        <li key={est._id} className="flex items-stretch">
                                            <button
                                                onClick={() => setSelectedId(est._id)}
                                                className={`flex min-w-0 flex-1 flex-col gap-0.5 border-l-2 px-4 py-2.5 text-left transition-colors ${
                                                    est._id === selectedId
                                                        ? "border-accent bg-surface-sunken"
                                                        : "border-transparent hover:bg-surface-sunken"
                                                }`}
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span className="truncate text-xs font-medium text-ink">{est.name}</span>
                                                    {est._id === linkedId && <Check className="h-3 w-3 flex-shrink-0 text-success" />}
                                                    {est.matchScore != null && (
                                                        <MatchScoreBadge score={est.matchScore} reasons={est.matchReasons} isDirectMatch={est.isDirectMatch} />
                                                    )}
                                                    {!est.is_active && (
                                                        <span className="rounded bg-danger-subtle px-1 py-px text-[9px] font-semibold uppercase text-danger">Fermé</span>
                                                    )}
                                                </div>
                                                <span className="flex items-center gap-1 text-[11px] text-ink-faint">
                                                    <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                                                    <span className="truncate">
                                                        {est.address?.city || "Ville inconnue"}
                                                        {est.address?.postcode ? ` (${est.address.postcode})` : ""}
                                                    </span>
                                                </span>
                                                {openedAgo(effectiveOpenDate(est)) && (
                                                    <span className={`flex items-center gap-1 text-[11px] ${sortKey === "date" ? "font-medium text-ink-muted" : "text-ink-faint"}`}>
                                                        <Clock className="h-2.5 w-2.5 flex-shrink-0" />
                                                        Ouvert {openedAgo(effectiveOpenDate(est))}
                                                    </span>
                                                )}
                                                {meta && (
                                                    <span className="truncate text-[10px] text-ink-faint">{meta}</span>
                                                )}
                                            </button>
                                            {pappers && (
                                                <a
                                                    href={pappers}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Ouvrir la fiche Pappers"
                                                    className="flex flex-shrink-0 items-center px-2 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink"
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {atLimit && !loading && (
                            <p className="px-4 pb-3 text-[10px] text-ink-faint">
                                {sortKey === "date"
                                    ? `Les ${results.length} ${sortDir === "desc" ? "plus récents" : "plus anciens"} — affinez pour élargir.`
                                    : "Liste tronquée — affinez les filtres pour la réduire."}
                            </p>
                        )}
                    </div>

                    <div className="min-w-0 flex-1 overflow-y-auto">
                        {selected ? (
                            <EstablishmentDetailPane
                                est={selected}
                                isLinked={selected._id === linkedId}
                                saving={saving}
                                articleCity={articleCity}
                                onLink={() => pick(selected)}
                                onUnlink={() => pick(null)}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center p-6 text-center text-xs text-ink-faint">
                                {loading ? "Chargement…" : "Sélectionnez un établissement pour voir sa fiche"}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

function EstablishmentLink({
    articleId,
    articleTitle,
    articleCity,
    articleLink,
    linked,
    onLink,
}: {
    articleId: string;
    articleTitle: string;
    articleCity: string | null;
    articleLink?: string;
    linked: LinkedEstablishment | null;
    onLink: (est: LinkedEstablishment | null) => Promise<void>;
}) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleUnlink = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setSaving(true);
        try { await onLink(null); } finally { setSaving(false); }
    };

    const handlePick = async (est: RichEstablishment | null) => {
        await onLink(est ? { _id: est._id, name: est.name, address: est.address ?? undefined } : null);
    };

    return (
        <div className="relative">
            {linked ? (
                <div className="inline-flex items-center gap-1 mt-1">
                    <span className="inline-flex items-center gap-1 text-[11px] text-ink bg-surface-sunken px-2 py-0.5 rounded-full font-medium">
                        <Link className="w-2.5 h-2.5" />
                        {linked.name}
                        {linked.address?.city && <span className="text-ink-muted">· {linked.address.city}</span>}
                    </span>
                    <button onClick={handleUnlink} disabled={saving} title="Dissocier"
                        className={`p-0.5 text-ink-faint hover:text-danger rounded transition-colors disabled:opacity-40 ${FOCUS_RING}`}>
                        <X className="w-3 h-3" />
                    </button>
                    <button onClick={() => setOpen(true)} title="Changer d'établissement"
                        className={`p-0.5 text-ink-faint hover:text-ink rounded transition-colors ${FOCUS_RING}`}>
                        <Pencil className="w-3 h-3" />
                    </button>
                </div>
            ) : (
                <button onClick={() => setOpen(true)}
                    className={`inline-flex items-center gap-1 mt-1 text-[11px] text-ink-faint hover:text-ink transition-colors rounded ${FOCUS_RING}`}
                    title="Lier un établissement">
                    <Link className="w-3 h-3" />
                    Lier un établissement
                </button>
            )}

            {open && (
                <EstablishmentPickerModal
                    articleId={articleId}
                    articleTitle={articleTitle}
                    articleCity={articleCity}
                    articleLink={articleLink}
                    linkedId={linked?._id ?? null}
                    onPick={handlePick}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
}

// ─── franchise tag ────────────────────────────────────────────────────────────

type FranchiseItem = NewsItem["franchise"];

function FranchiseRow({ f, onClick, disabled, highlight }: { f: FranchiseDoc; onClick: () => void; disabled: boolean; highlight?: boolean }) {
    return (
        <button onClick={onClick} disabled={disabled}
            className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-surface-sunken transition-colors disabled:opacity-50 ${highlight ? "text-ink font-medium" : "text-ink-muted"}`}>
            {f.logoUrl
                ? <img src={f.logoUrl} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                : <span className="w-4 h-4 rounded-full bg-surface-sunken flex-shrink-0" />
            }
            {f.name}
        </button>
    );
}

function FranchiseTag({
    franchise,
    allFranchises,
    articleText,
    onSave,
}: {
    franchise: FranchiseItem;
    allFranchises: FranchiseDoc[];
    articleText: string;
    onSave: (name: string | null) => Promise<void>;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setTimeout(() => inputRef.current?.focus(), 50);
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const lowerText = articleText.toLowerCase();
    const currentName = franchise?.name ?? null;

    const suggested = allFranchises.filter((f) =>
        f.name !== currentName && lowerText.includes(f.name.toLowerCase())
    );
    const filtered = query.trim()
        ? allFranchises.filter((f) => f.name.toLowerCase().includes(query.toLowerCase()))
        : allFranchises.filter((f) => !suggested.includes(f));

    const handleSelect = async (name: string) => {
        setSaving(true);
        try { await onSave(name); setOpen(false); } finally { setSaving(false); }
    };

    const handleRemove = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setSaving(true);
        try { await onSave(null); } finally { setSaving(false); }
    };

    const handleFreeText = async () => {
        const val = query.trim();
        if (!val) return;
        await handleSelect(val);
    };

    return (
        <div className="relative inline-block" ref={panelRef}>
            {franchise ? (
                <div className="inline-flex items-center gap-1 mt-1">
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-ink bg-surface-sunken px-2 py-0.5 rounded-full font-medium">
                        {franchise.logoUrl && (
                            <img src={franchise.logoUrl} alt="" className="w-3 h-3 rounded-full object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        )}
                        {franchise.name}
                    </span>
                    <button onClick={handleRemove} disabled={saving} title="Retirer"
                        className={`p-0.5 text-ink-faint hover:text-danger rounded transition-colors disabled:opacity-40 ${FOCUS_RING}`}>
                        <X className="w-3 h-3" />
                    </button>
                    <button onClick={() => setOpen((o) => !o)} title="Modifier"
                        className={`p-0.5 text-ink-faint hover:text-ink rounded transition-colors ${FOCUS_RING}`}>
                        <Pencil className="w-3 h-3" />
                    </button>
                </div>
            ) : (
                <button onClick={() => setOpen((o) => !o)}
                    className={`inline-flex items-center gap-0.5 mt-1 text-[11px] text-ink-faint hover:text-ink transition-colors rounded ${FOCUS_RING}`}
                    title="Tagger une franchise">
                    <Plus className="w-3 h-3" />franchise
                </button>
            )}

            {open && (
                <div className="absolute left-0 top-full mt-1.5 z-30 w-64 bg-surface border border-border rounded-2xl shadow-float overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                        <Search className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleFreeText(); if (e.key === "Escape") setOpen(false); }}
                            placeholder="Nom de franchise…"
                            className="flex-1 text-xs bg-transparent text-ink placeholder:text-ink-faint outline-none"
                        />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {!query.trim() && suggested.length > 0 && (
                            <>
                                <p className="px-3 pt-2 pb-1 text-[10px] text-ink-faint uppercase tracking-wide font-semibold">Trouvé dans l'article</p>
                                {suggested.map((f) => (
                                    <FranchiseRow key={f._id} f={f} onClick={() => handleSelect(f.name)} disabled={saving} highlight />
                                ))}
                                <div className="border-t border-border my-1" />
                            </>
                        )}
                        {filtered.map((f) => (
                            <FranchiseRow key={f._id} f={f} onClick={() => handleSelect(f.name)} disabled={saving} />
                        ))}
                        {query.trim() && !allFranchises.some((f) => f.name.toLowerCase() === query.trim().toLowerCase()) && (
                            <button onClick={handleFreeText} disabled={saving}
                                className="w-full text-left px-3 py-1.5 text-xs text-ink-muted hover:bg-surface-sunken transition-colors disabled:opacity-50 border-t border-border">
                                Ajouter «&nbsp;{query.trim()}&nbsp;»
                            </button>
                        )}
                        {filtered.length === 0 && suggested.length === 0 && !query.trim() && (
                            <p className="px-3 py-3 text-xs text-ink-faint text-center">Aucune franchise connue</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── article row ──────────────────────────────────────────────────────────────

export function ArticleRow({
    item,
    allFranchises,
    onSetStatus,
    onSetCity,
    onSetCategory,
    onSetImage,
    onScrapeImages,
    onSetEstablishment,
    onSetFranchise,
}: {
    item: NewsItem;
    allFranchises: FranchiseDoc[];
    onSetStatus: (id: string, status: NewsStatus) => Promise<void>;
    onSetCity: (id: string, city: string | null) => Promise<void>;
    onSetCategory: (id: string, category: "opening" | "closing" | "ownership" | "recruitment") => Promise<void>;
    onSetImage: (id: string, image: string | null) => Promise<void>;
    onScrapeImages: (id: string) => Promise<void>;
    onSetEstablishment: (id: string, est: LinkedEstablishment | null) => Promise<void>;
    onSetFranchise: (id: string, franchise: string | null) => Promise<void>;
}) {
    const [loading, setLoading] = useState(false);

    const handle = async (status: NewsStatus) => {
        setLoading(true);
        try {
            await onSetStatus(item._id, status);
        } finally {
            setLoading(false);
        }
    };

    const style = STATUS_STYLE[item.status] ?? STATUS_STYLE.pending;

    return (
        <div className={`flex gap-3 py-3 px-3 rounded-lg transition-colors duration-150 ${style.rowBg}`}>
            {/* thumb */}
            <ImagePicker
                image={item.image}
                scrapedImages={item.scrapedImages ?? []}
                imagesScrapedAt={item.imagesScrapedAt}
                scrapeOutcome={item.imageScrapeOutcome}
                scrapeDetail={item.imageScrapeDetail}
                onSave={(image) => onSetImage(item._id, image)}
                onScrape={() => onScrapeImages(item._id)}
            />

            {/* content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <CategoryEdit category={item.category as "opening" | "closing" | "ownership" | "recruitment"} onSave={(category) => onSetCategory(item._id, category)} />
                    <ScoreBadge score={item.aiScore} />
                    <JevScoreBadge jev={item.jev} />
                    <JevEventChip jev={item.jev} />
                    <JevReviewBadge jev={item.jev} />
                    {item.isFranchise === true && (
                        <span className="text-[10px] text-ink-muted bg-surface-sunken px-1.5 py-0.5 rounded font-medium">Franchise</span>
                    )}
                    {item.isInFrance === false && (
                        <span className="text-[10px] text-ink-muted bg-surface-sunken px-1.5 py-0.5 rounded font-medium">Hors France</span>
                    )}
                    <CityEdit city={item.city} onSave={(city) => onSetCity(item._id, city)} />
                    <span className="text-[11px] text-ink-faint">{timeAgo(item.published)}</span>
                    {item.source.favicon && (
                        <img src={item.source.favicon} alt="" className="w-3.5 h-3.5 rounded-full"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    )}
                    <span className="text-[11px] text-ink-faint truncate max-w-[120px]">{item.source.name || item.source.domain}</span>
                    <SourceScrapeBadge outcome={item.imageScrapeOutcome} detail={item.imageScrapeDetail} />
                </div>
                <p className={`text-xs font-medium leading-snug line-clamp-2 ${style.titleCls}`}>
                    {item.title}
                </p>
                <JevExtraction jev={item.jev} articleCity={item.city} onApplyCity={(city) => onSetCity(item._id, city)} />
                <div className="flex flex-wrap gap-x-3">
                    <EstablishmentLink
                        articleId={item._id}
                        articleTitle={item.title}
                        articleCity={item.city}
                        articleLink={item.link}
                        linked={item.linkedEstablishment}
                        onLink={(est) => onSetEstablishment(item._id, est)}
                    />
                    <FranchiseTag
                        franchise={item.franchise}
                        allFranchises={allFranchises}
                        articleText={`${item.title} ${item.description}`}
                        onSave={(franchise) => onSetFranchise(item._id, franchise)}
                    />
                </div>
            </div>

            {/* actions */}
            <div className="flex-shrink-0 flex items-center gap-1.5">
                <a href={item.link} target="_blank" rel="noopener noreferrer"
                    className={`p-1.5 text-ink-faint hover:text-ink rounded-md hover:bg-surface-sunken transition-colors ${FOCUS_RING}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                </a>
                {/* Approve */}
                <button
                    onClick={() => handle("approved")}
                    disabled={loading || item.status === "approved"}
                    title="Approuver"
                    className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${FOCUS_RING} ${
                        item.status === "approved"
                            ? "text-success bg-success-subtle"
                            : "text-ink-faint hover:text-success hover:bg-success-subtle"
                    }`}
                >
                    <Check className="w-3.5 h-3.5" />
                </button>
                {/* Reject */}
                <button
                    onClick={() => handle("rejected")}
                    disabled={loading || item.status === "rejected"}
                    title="Rejeter"
                    className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${FOCUS_RING} ${
                        item.status === "rejected"
                            ? "text-danger bg-danger-subtle"
                            : "text-ink-faint hover:text-danger hover:bg-danger-subtle"
                    }`}
                >
                    <Ban className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}
